import { z } from 'zod';

import { organizationSchema } from '../organization';
import { planSchema } from '../organization-billing';
import { CancelReason } from '../organization-billing';

// Plans list
export const plansListReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z
    .object({
      oid: z.string().nullish(),
    })
    .nullish(),
  body: z.object({}).nullish(),
});
export type PlansListReq = z.infer<typeof plansListReqSchema>;

export const plansListResSchema = {
  200: z.object({
    data: z.object({
      plans: z.array(planSchema),
    }),
  }),
};
export type PlansListRes = z.infer<(typeof plansListResSchema)[200]>;

// Subscribe
export const subscribeReqSchema = z.object({
  params: z.object({
    organizationId: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({
    planId: z.string().min(5),
    discountCoupon: z.string().nullish(),
    toltReferral: z.string().nullish(),
  }),
});
export type SubscribeReq = z.infer<typeof subscribeReqSchema>;

export const subscribeResSchema = {
  200: z.object({
    data: z.object({
      checkout: z.string(),
    }),
  }),
};
export type SubscribeRes = z.infer<(typeof subscribeResSchema)[200]>;

// Update
export const updateReqSchema = z.object({
  params: z.object({
    organizationId: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({
    planId: z.string().min(5),
    discountCoupon: z.string().nullish(),
  }),
});
export type UpdateReq = z.infer<typeof updateReqSchema>;

export const updateResSchema = {
  200: z.object({
    data: z.object({
      immediate: z.boolean(),
      effectiveAt: z.number().optional(),
      redirectUrl: z.string().optional(),
    }),
  }),
};
export type UpdateRes = z.infer<(typeof updateResSchema)[200]>;

// Cancel subscription
export const cancelSubscriptionReqSchema = z.object({
  params: z.object({
    organizationId: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z
    .object({
      reason: z.nativeEnum(CancelReason),
      extraDetails: z.string(),
    })
    .nullish(),
});
export type CancelSubscriptionReq = z.infer<typeof cancelSubscriptionReqSchema>;

export const cancelSubscriptionResSchema = {
  200: z.object({ ok: z.boolean() }),
};
export type CancelSubscriptionRes = z.infer<(typeof cancelSubscriptionResSchema)[200]>;

// Uncancel subscription
export const uncancelSubscriptionReqSchema = z.object({
  params: z.object({
    organizationId: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type UncancelSubscriptionReq = z.infer<typeof uncancelSubscriptionReqSchema>;

export const uncancelSubscriptionResSchema = {
  200: z.object({
    data: z.object({}).nullish(),
    err: z.string().nullish(),
    success: z.boolean().nullish(),
  }),
};
export type UncancelSubscriptionRes = z.infer<(typeof uncancelSubscriptionResSchema)[200]>;

// End trial
export const endTrialReqSchema = z.object({
  params: z.object({ organizationId: organizationSchema.shape._id }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type EndTrialReq = z.infer<typeof endTrialReqSchema>;

export const endTrialResSchema = {
  200: z.object({ ok: z.boolean() }),
};
export type EndTrialRes = z.infer<(typeof endTrialResSchema)[200]>;

// Request billing portal url
export const requestBillingPortalUrlReqSchema = z.object({
  params: z.object({
    organizationId: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type RequestBillingPortalUrlReq = z.infer<typeof requestBillingPortalUrlReqSchema>;

export const requestBillingPortalUrlResSchema = {
  200: z.object({
    url: z.string(),
    id: z.string(),
  }),
};
export type RequestBillingPortalUrlRes = z.infer<(typeof requestBillingPortalUrlResSchema)[200]>;

// Buy extra credits
export type BuyCreditsRes = {
  data: {
    checkout: string;
    sessionId: string;
  };
};
